import { useState, useCallback, useEffect } from 'react';
import { useLazyGetInvoiceApprovalsQuery, useUpdateInvoiceApprovalMutation, useGetInvoiceApprovalByIdQuery, useGetInvoiceApprovalPreviewMutation } from '../../../../../api/endpoints/invoiceApproval';

import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateForApi } from '../../../../../utils/dateUtils';

export const useInvoiceApproval = () => {
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(1);
        return date;
    });

    const [endDate, setEndDate] = useState(() => {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        date.setDate(0);
        return date;
    });

    const { getCurrentLanguageId, th } = useLanguage();

    const [selectedDealers, setSelectedDealers] = useState([]);
    const [selectedWarrantyTypes, setSelectedWarrantyTypes] = useState([]);
    const [invoiceData, setInvoiceData] = useState([]);
    const [selectedInvoices, setSelectedInvoices] = useState([]);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [previewError, setPreviewError] = useState(false);

    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0
    });

    const [fetchPreview, { isLoading: isLoadingPreview }] = useGetInvoiceApprovalPreviewMutation();


    const [fetchInvoices, { isLoading: isLoadingInvoices, isError: isInvoiceError }] = useLazyGetInvoiceApprovalsQuery();

    const { data: invoiceDetailsResponse, isLoading: isLoadingDetails } = useGetInvoiceApprovalByIdQuery(selectedInvoiceId, {
        skip: !selectedInvoiceId
    });

    const [updateInvoiceApproval, { isLoading: isUpdating }] = useUpdateInvoiceApprovalMutation();

    const [searchParams, setSearchParams] = useState({});

    const [lastApprovalResult, setLastApprovalResult] = useState(null);

    const handleResetFilters = useCallback(() => {
        const firstDay = new Date();
        firstDay.setDate(1);
        const lastDay = new Date();
        lastDay.setMonth(lastDay.getMonth() + 1);
        lastDay.setDate(0);

        setStartDate(firstDay);
        setEndDate(lastDay);
        setSelectedDealers([]);
        setSelectedWarrantyTypes([]);
        setInvoiceData([]);
        setSelectedInvoices([]);
        setSearchParams({});
        setTotalRecords(0);
        setLazyParams({ first: 0, rows: 10, page: 0 });
        setLastApprovalResult(null);
    }, []);

    const onRowCheckboxChange = useCallback((e, rowData) => {
        setInvoiceData((prevData) => prevData.map((item) => (item.id === rowData.id ? { ...item, selected: e.checked } : item)));

        setSelectedInvoices((prevSelected) => {
            if (e.checked) {
                return [...prevSelected, rowData];
            } else {
                return prevSelected.filter((item) => item.id !== rowData.id);
            }
        });
    }, []);

    const onSelectAllChange = useCallback(
        (e) => {
            const allSelected = e.checked;
            setInvoiceData((prevData) =>
                prevData.map((item) => ({
                    ...item,
                    selected: allSelected
                }))
            );
            setSelectedInvoices(allSelected ? [...invoiceData] : []);
        },
        [invoiceData]
    );

    const handleInfoClick = useCallback((customerStatementId) => {
        setSelectedInvoiceId(customerStatementId);
        setDetailModalVisible(true);
    }, []);

    const handleCloseDetailModal = useCallback(() => {
        setDetailModalVisible(false);
        setSelectedInvoiceId(null);
    }, []);

    const processResponse = (result) => {
        if (result.data?.data?.items && Array.isArray(result.data.data.items)) {
            const processedData = result.data.data.items.map((item) => ({
                ...item,
                selected: false,
                uniqueKey: item.id
            }));
            setInvoiceData(processedData);
            setTotalRecords(result.data.data.totalCount || 0);
        } else {
            setInvoiceData([]);
            setTotalRecords(0);
        }
    };

    const performSearch = useCallback(
        async (dealerFilter, warrantyTypeFilter, domesticType = null, overrideStartDate, overrideEndDate) => {
            setInvoiceData([]);
            setSelectedInvoices([]);
            setLastApprovalResult(null);
            setSelectedDealers(dealerFilter?.isAllDealers ? [] : dealerFilter?.dealerIds || []);
            setSelectedWarrantyTypes(warrantyTypeFilter?.isAllWarrTypes ? [] : warrantyTypeFilter?.warrTypeIds || []);

            setLazyParams({ first: 0, rows: 10, page: 0 });

            const effectiveStartDate = overrideStartDate || startDate;
            const effectiveEndDate = overrideEndDate || endDate;

            const params = {
                pageNumber: 1,
                pageSize: 10,
                startDate: formatDateForApi(effectiveStartDate),
                endDate: formatDateForApi(effectiveEndDate),
                dealerList: dealerFilter?.isAllDealers ? [] : dealerFilter?.dealerIds || [],
                warrTypeIds: warrantyTypeFilter?.isAllWarrTypes ? [] : warrantyTypeFilter?.warrTypeIds || [],
                isAllDealers: !!dealerFilter?.isAllDealers,
                isAllWarrTypes: !!warrantyTypeFilter?.isAllWarrTypes,
                domesticType: domesticType
            };

            setSearchParams(params);

            const result = await fetchInvoices(params);
            processResponse(result);
            return result;
        },
        [fetchInvoices, startDate, endDate]
    );

    const handlePageChange = useCallback(
        async (event) => {
            setLazyParams(event);

            const params = {
                ...searchParams,
                pageNumber: event.page + 1,
                pageSize: event.rows
            };

            setSearchParams(params);

            const result = await fetchInvoices(params);
            processResponse(result);
        },
        [fetchInvoices, searchParams]
    );

    const refreshInvoiceList = useCallback(async () => {
        if (Object.keys(searchParams).length > 0) {
            const result = await fetchInvoices(searchParams);

            if (result.data?.data?.items && Array.isArray(result.data.data.items)) {
                const processedData = result.data.data.items.map((item) => ({
                    ...item,
                    selected: false
                }));
                setInvoiceData(processedData);
                setSelectedInvoices([]);
                setTotalRecords(result.data.data.totalCount || 0);
            } else {
                setInvoiceData([]);
                setSelectedInvoices([]);
                setTotalRecords(0);
            }
            return result;
        }
        return null;
    }, [fetchInvoices, searchParams]);

    const buildApprovalResultMessage = useCallback((resultData, isApproveAll = false) => {
        const { totalCount, successCount, failCount } = resultData;

        if (failCount === 0) {
            return {
                success: true,
                severity: 'success',
                message: isApproveAll
                    ? th('invoiceApproval.messages.allApprovedSuccess')
                    : th('invoiceApproval.messages.approvalSuccess').replace('{count}', successCount),
                resultData
            };
        }

        if (successCount === 0) {
            return {
                success: false,
                severity: 'error',
                message: th('invoiceApproval.messages.allApprovalFailed'),
                resultData
            };
        }

        return {
            success: true,
            severity: 'warn',
            message: th('invoiceApproval.messages.partialApproval')
                .replace('{totalCount}', totalCount)
                .replace('{successCount}', successCount)
                .replace('{failCount}', failCount),
            resultData
        };
    }, [th]);

    const handleApproval = useCallback(
        async (selectedItems = null, note = '') => {
            const itemsToApprove = selectedItems || selectedInvoices;
            if (itemsToApprove.length === 0) return { success: false, severity: 'warn', message: th('invoiceApproval.messages.noRecordsSelected') };

            try {
                const approvalData = {
                    ClaimsData: itemsToApprove.map((invoice) => ({
                        customerStatementId: invoice.customerStatementId,
                        id: invoice.id,
                        warrClaimsNo: invoice.warrantyClaimNo
                    })),
                    LanguageId: getCurrentLanguageId(),
                    IsApproveAll: false,
                    Note: note || null
                };

                const resultData = await updateInvoiceApproval(approvalData).unwrap();
                setSelectedInvoices([]);
                await refreshInvoiceList();

                if (resultData && resultData.totalCount !== undefined) {
                    setLastApprovalResult(resultData);
                    return buildApprovalResultMessage(resultData, false);
                }

                return { success: true, severity: 'success', message: th('invoiceApproval.messages.approvalSuccess').replace('{count}', itemsToApprove.length), resultData: null };
            } catch (error) {
                console.error('Approval error:', error);
                return { success: false, severity: 'error', message: th('invoiceApproval.messages.approvalFailed'), resultData: null };
            }
        },
        [selectedInvoices, updateInvoiceApproval, getCurrentLanguageId, refreshInvoiceList, th, buildApprovalResultMessage]
    );

    const handleApproveAll = useCallback(async (note = '') => {
        if (!note || !note.trim()) return { success: false, severity: 'warn', message: th('invoiceApproval.confirmations.noteRequired'), resultData: null };
        
        try {
            const approvalData = {
                IsApproveAll: true,
                StartDate: formatDateForApi(startDate),
                EndDate: formatDateForApi(endDate),
                DealerList: searchParams.dealerList || [],
                WarrTypeIds: searchParams.warrTypeIds || [],
                IsAllDealers: searchParams.isAllDealers || false,
                IsAllWarrTypes: searchParams.isAllWarrTypes || false,
                LanguageId: getCurrentLanguageId(),
                Note: note.trim(),
                DomesticType: searchParams.domesticType ?? null
            };

            const resultData = await updateInvoiceApproval(approvalData).unwrap();
            setSelectedInvoices([]);
            await refreshInvoiceList();

            if (resultData && resultData.totalCount !== undefined) {
                setLastApprovalResult(resultData);
                return buildApprovalResultMessage(resultData, true);
            }

            return { success: true, severity: 'success', message: th('invoiceApproval.messages.allApprovedSuccess'), resultData: null };
        } catch (error) {
            console.error('ApproveAll error:', error);
            return { success: false, severity: 'error', message: th('invoiceApproval.messages.allApprovalFailed'), resultData: null };
        }
    }, [updateInvoiceApproval, startDate, endDate, searchParams, getCurrentLanguageId, refreshInvoiceList, th, buildApprovalResultMessage]);

    const handlePreview = useCallback(async (isApproveAll = false) => {
        setPreviewData(null);
        setPreviewError(false);

        try {
            let previewRequest;

            if (isApproveAll) {
                previewRequest = {
                    IsApproveAll: true,
                    StartDate: formatDateForApi(startDate),
                    EndDate: formatDateForApi(endDate),
                    DealerList: searchParams.dealerList || [],
                    WarrTypeIds: searchParams.warrTypeIds || [],
                    IsAllDealers: searchParams.isAllDealers || false,
                    IsAllWarrTypes: searchParams.isAllWarrTypes || false,
                    DomesticType: searchParams.domesticType ?? null
                };
            } else {
                const warrClaimsNos = selectedInvoices
                    .map(inv => inv.warrantyClaimNo)
                    .filter(Boolean);

                previewRequest = {
                    IsApproveAll: false,
                    StartDate: formatDateForApi(startDate),
                    EndDate: formatDateForApi(endDate),
                    WarrClaimsNos: warrClaimsNos,
                    DealerList: [],
                    WarrTypeIds: [],
                    IsAllDealers: false
                };
            }

            const result = await fetchPreview(previewRequest).unwrap();
            setPreviewData(result);
            return result;
        } catch (error) {
            console.error('Preview error:', error);
            setPreviewError(true);
            return null;
        }
    }, [fetchPreview, selectedInvoices, startDate, endDate, searchParams]);

    const clearApprovalResult = useCallback(() => {
        setLastApprovalResult(null);
    }, []);

    const formatCurrency = useCallback((amount, currency = 'TRY') => {
        if (amount === null || amount === undefined || isNaN(amount)) return '-';
        const validCurrency = currency || 'TRY';
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: validCurrency
        }).format(amount);
    }, []);

    return {
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        invoiceData,
        selectedInvoices,
        isLoadingInvoices,
        isUpdating,
        isInvoiceError,
        onRowCheckboxChange,
        onSelectAllChange,
        handleApproval,
        handleApproveAll,
        formatCurrency,
        performSearch,
        handleResetFilters,
        detailModalVisible,
        invoiceDetails: invoiceDetailsResponse,
        isLoadingDetails,
        handleInfoClick,
        handleCloseDetailModal,
        refreshInvoiceList,
        totalRecords,
        lazyParams,
        handlePageChange,
        lastApprovalResult,
        clearApprovalResult,
        handlePreview,
        previewData,
        isLoadingPreview,
        previewError
    };
};
